import { useQueryClient } from "@tanstack/react-query";
import useKlydoMutation, {
  UseKlydoMutationOptions,
} from "../../../../components/hooks/useKlydoMutation";
import uploadsApiClient, {
  UploadFileInput,
  UploadResponse,
} from "../../../../api/uploads";

const useUploadProfileImageMutation = (
  options: Omit<
    UseKlydoMutationOptions<
      UploadResponse,
      UploadFileInput,
      { previousUploadStatus: UploadResponse | undefined }
    >,
    "onSuccess" | "onSettled"
  > & {
    onSuccess?: (response: UploadResponse) => void;
    onSettled?: (response: UploadResponse | undefined, error: unknown) => void;
  } = {},
) => {
  const queryClient = useQueryClient();

  return useKlydoMutation<
    UploadResponse,
    UploadFileInput,
    { previousUploadStatus: UploadResponse | undefined }
  >({
    mutationFn: (input: UploadFileInput) =>
      uploadsApiClient.uploadProfileImage(input),
    mutationKey: ["uploadProfileImage"],
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ["uploadProfileImage"] });

      const previousUploadStatus: UploadResponse | undefined =
        queryClient.getQueryData(["uploadProfileImage"]);

      options.onMutate?.(input);

      return { previousUploadStatus };
    },
    onError: (error, input, context) => {
      if (context?.previousUploadStatus) {
        queryClient.setQueryData(
          ["uploadProfileImage"],
          context.previousUploadStatus,
        );
      }
      options.onError?.(error, input, context);
    },
    onSettled: async (response, error) => {
      if (options.onSettled) {
        options.onSettled(response, error);
      }
      await queryClient.invalidateQueries({ queryKey: ["giftOrders"] });
    },
    onSuccess: (response) => {
      if (options.onSuccess) {
        options.onSuccess(response);
      }
    },
    ...options,
  });
};

export default useUploadProfileImageMutation;
